// HdNumberField — the canonical Helpdesk number stepper.
//
// Translated from docs/design_handoff_helpdesk/prototype/primitives.jsx
// `NumberStepper` (line 277) and its shared `inputBase` (line 234). Every
// number below traces to those two blocks; colours are read as CSS custom
// properties from the .helpdesk-shell block, never hard-coded.
//
// WHY THIS EXISTS: the design defines NumberStepper as a compact bordered
// control with a mono, tabular-nums value and a stacked chevron-up /
// chevron-down column. The implementation instead used raw
// <TextField type="number"> at ~21 sites, which renders the browser's own
// grey spinner arrows and a proportional-font value — a different control,
// not just an off-token one. This component is the one true translation;
// route every bounded numeric setting (SLA minutes, sort order, complexity,
// percentages) through it.
//
// Deliberately NOT adopted everywhere `type="number"` appears — several
// sites are free-entry or unbounded (time-log minutes, tenant-defined custom
// fields, external calendar IDs) where a stepper controlling the value one
// unit at a time is the wrong interaction. Those were left as TextField.
//
// Built directly on a native <input type="number">, not MUI's TextField —
// the design's control is not a TextField with icons swapped in, it is its
// own shape (value + chevron column, no outline "notch"). Wrapping TextField
// would fight its InputAdornment layout for very little gain.
import React, { useRef } from 'react';
import { Box } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// React 17 here (no `useId` export) — a module-level counter gives a stable,
// unique id per mounted instance instead.
let instanceCounter = 0;

export interface HdNumberFieldProps {
  value: number | '';
  onChange: (next: number | '') => void;
  min?: number;
  max?: number;
  /** Amount the chevrons / arrow keys move the value by. Defaults to 1. */
  step?: number;
  suffix?: string;
  /** primitives.jsx:277 default `w = 110`. */
  width?: number | string;
  disabled?: boolean;
  placeholder?: string;
  error?: boolean;
  id?: string;
  name?: string;
  'aria-label'?: string;
}

function clamp(n: number, min?: number, max?: number): number {
  let out = n;
  if (min != null && out < min) out = min;
  if (max != null && out > max) out = max;
  return out;
}

export default function HdNumberField({
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
  width = 110,
  disabled,
  placeholder,
  error,
  id,
  name,
  'aria-label': ariaLabel
}: HdNumberFieldProps) {
  const reactIdRef = useRef<number>();
  if (reactIdRef.current == null) {
    instanceCounter += 1;
    reactIdRef.current = instanceCounter;
  }
  const inputId = id || `hd-number-field-${reactIdRef.current}`;

  // The field is driven by its own string state, not the numeric `value` prop
  // directly. A native `<input type="number">` bound straight to a number lets
  // React's loose-equality reconciliation treat "020" and "20" as equal, so a
  // stray leading zero the user types is never corrected on screen. Holding
  // the display string here (and normalising it on blur / external change)
  // keeps what's shown canonical — "020" becomes "20", a cleared field shows
  // the canonical value again rather than staying blank.
  const canonical = value === '' ? '' : String(value);
  const [text, setText] = React.useState<string>(canonical);
  const focusedRef = useRef(false);

  // Re-sync when `value` changes from OUTSIDE (parent reset, the chevrons, a
  // clamp) — but never mid-type, so we don't fight the user's cursor.
  // `canonical` is the only dependency that matters here; it is derived from
  // `value` each render, so this covers every external change.
  React.useEffect(() => {
    if (!focusedRef.current) setText(canonical);
  }, [canonical]);

  const stepValue = (direction: 1 | -1) => {
    if (disabled) return;
    const base = typeof value === 'number' ? value : min ?? 0;
    const next = clamp(base + direction * step, min, max);
    setText(String(next)); // keep the field in sync even while it has focus (arrow keys)
    onChange(next);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // type="text" would otherwise accept anything — every HdNumberField in
    // this app is a non-negative whole number (minutes, %, counts, order), so
    // strip to digits only. That also makes inputMode="numeric" honest.
    const raw = e.target.value.replace(/\D/g, '');
    setText(raw);
    if (raw === '') {
      onChange('');
      return;
    }
    const parsed = Number(raw);
    if (!Number.isNaN(parsed)) onChange(parsed);
  };

  const handleFocus = () => {
    focusedRef.current = true;
  };

  const handleBlur = () => {
    focusedRef.current = false;
    setText(canonical); // "020" -> "20"; "" -> canonical value
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      stepValue(1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      stepValue(-1);
    }
  };

  const canDecrease = !disabled && !(typeof value === 'number' && min != null && value <= min);
  const canIncrease = !disabled && !(typeof value === 'number' && max != null && value >= max);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'stretch',
        width,
        // inputBase — primitives.jsx:234-235, padding overridden to 0
        // (the value span and chevron column carry their own padding).
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        color: 'var(--ink)',
        backgroundColor: disabled ? 'var(--card-sub)' : 'var(--card)',
        border: `1px solid ${error ? 'var(--status-danger-fg)' : 'var(--hairline-strong)'}`,
        borderRadius: 'var(--r-sm)',
        boxShadow: error ? '0 0 0 3px var(--status-danger-bg)' : 'none',
        lineHeight: 1.4,
        opacity: disabled ? 0.6 : 1
      }}
    >
      <Box
        component="label"
        htmlFor={inputId}
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          padding: '7px 10px',
          minWidth: 0,
          cursor: disabled ? 'default' : 'text'
        }}
      >
        <Box
          component="input"
          id={inputId}
          name={name}
          // type="text" (not "number"): a controlled number input lets React's
          // loose-equality reconciliation keep a stray "020" on screen. We
          // manage the string ourselves and normalise on blur.
          type="text"
          inputMode="numeric"
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={ariaLabel}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          sx={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            padding: 0,
            fontFamily: 'var(--font-mono)',
            fontVariantNumeric: 'tabular-nums',
            fontSize: 13,
            color: 'inherit',
            // Hide the native spinner — the chevron column replaces it.
            '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
              margin: 0
            },
            MozAppearance: 'textfield'
          }}
        />
        {suffix && (
          <Box component="span" sx={{ color: 'var(--muted)', fontSize: 11.5, whiteSpace: 'nowrap' }}>
            {suffix}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '1px solid var(--hairline)'
        }}
      >
        <Box
          component="button"
          type="button"
          aria-label="Increase"
          disabled={!canIncrease}
          onClick={() => stepValue(1)}
          sx={{
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            padding: '0 7px',
            color: 'var(--muted)',
            border: 'none',
            borderBottom: '1px solid var(--hairline)',
            background: 'transparent',
            cursor: canIncrease ? 'pointer' : 'default',
            opacity: canIncrease ? 1 : 0.4,
            '&:hover': canIncrease ? { color: 'var(--ink-2)' } : undefined
          }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: 13 }} />
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Decrease"
          disabled={!canDecrease}
          onClick={() => stepValue(-1)}
          sx={{
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            padding: '0 7px',
            color: 'var(--muted)',
            border: 'none',
            background: 'transparent',
            cursor: canDecrease ? 'pointer' : 'default',
            opacity: canDecrease ? 1 : 0.4,
            '&:hover': canDecrease ? { color: 'var(--ink-2)' } : undefined
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 13 }} />
        </Box>
      </Box>
    </Box>
  );
}
